import React, { useState } from "react";

import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  FileInput,
  Group,
  Image,
  NumberInput,
  Radio,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Dropzone, IMAGE_MIME_TYPE, MIME_TYPES } from "@mantine/dropzone";
import { IconPhoto, IconMinus, IconX } from "@tabler/icons";
import { useRouter } from "next/router";
import getStripe from "../utils/getStripe";
import { fetchPostJSON } from "../utils/apiHelpers";
import RichText from "../components/RichText";

const readFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
  });
};
const encodeData = (data) => {
  const formData = new FormData();

  Object.keys(data).forEach((key) => {
    if (data[key]) {
      formData.append(key, data[key]);
    }
  });

  return formData;
};

// function encode(data) {
//   return Object.keys(data)
//     .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
//     .join("&");
// }

const jobTypes = ["Full-time", "Part-time", "Contract"];
const SubmitJob = ({ data, location }) => {
  const router = useRouter();
  const [jobType, setJobType] = useState("Full-time");
  const [jobLocationType, setJobLocationType] = useState("remote");
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoImageType, setLogoImageType] = useState(null);
  const [logoExt, setLogoExt] = useState(null);
  const [logoFileErrors, setLogoFileErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    initialValues: {
      email: "",
      jobType: "Full-time",
      jobLocationType: "remote",
      title: "",
      description: "",
      applicationUrl: "",
      company: "",
      location: "",
      minSalary: null,
      maxSalary: null,
    },

    validate: {
      title: (value) => (value.length < 1 ? "Title must not be empty" : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      applicationUrl: (value) => (value.length < 1 ? "Application URL must not be empty" : null),
      company: (value) => (value.length < 1 ? "Company name is required" : null),
      location: (value) => (value.length < 1 && jobLocationType !== "remote" ? "Location is required when job is Onsite" : null),
    },
  });

  const companyLogoPreview = companyLogo ? URL.createObjectURL(companyLogo) : null;

  const setLogo = async (files) => {
    setLogoFileErrors([]);
    const file = files[0];

    const fileExt = file.name.split(".").pop();

    const logo = await readFile(file);
    setLogoImageType(file.type);
    setLogoFile(logo);
    setLogoExt(fileExt);
    setCompanyLogo(file);
  };

  const theme = useMantineTheme();

  return (
    <Box>
      <h2>Reach talented React Native developers.</h2>
      <Box pb="lg">
        <Group>
          <Text>
            Your listing will be live for 30 days,{" "}
            <Badge variant="filled" color={"yellow"}>
              Featured
            </Badge>{" "}
            at the top of the job list with a gold border, and included in the weekly newsletter.
          </Text>
        </Group>
        <Text pt="md">
          Have a question?{" "}
          <Text variant="link" component="a" weight={"400"} mb="lg" href="mailto:jimmy@reactnativejobs.com">
            Send me an email
          </Text>
        </Text>
      </Box>
      <form
        id="submit-job-form"
        name="submit-job-form"
        method="post"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        onSubmit={form.onSubmit(async (values) => {
          setLoading(true);
          fetch("/api/submit-job", {
            method: "POST",
            body: JSON.stringify({
              ...values,
              jobLocationType,
              jobType,
              logoFile,
              logoImageType,
              logoExt,
            }),
          })
            .then(async (res) => {
              const response = await res.json();

              console.log("response", response);
              if (response.status === 500) {
                console.error(response.message);
                alert("Error submitting form. Contact me at jimmy@reactnativejobs.com for help.");
                return;
              }
              router.push("submit-success");
              setLoading(false);
            })
            .catch((error) => {
              setLoading(false);
              alert(error);
            });
        })}
      >
        <Stack spacing="xl">
          <TextInput name="title" placeholder="Job Title" label="Job Title" {...form.getInputProps("title")} />
          <Box>
            <Text
              size={"sm"}
              weight={500}
              sx={(theme) => ({
                display: "inline-block",
                color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.gray[9],
                wordBreak: "break-word",
                cursor: "default",
                WebkitTapHighlightColor: "transparent",
              })}
            >
              Job Description
            </Text>
            <RichText
              value={"<p>Add details about the job</p>"}
              controls={[
                ["bold", "italic", "underline", "link", "image"],
                ["unorderedList", "h1", "h2", "h3"],
                ["alignLeft", "alignCenter", "alignRight"],
              ]}
              aria-label="Job Description"
              {...form.getInputProps("description")}
            />
          </Box>
          <TextInput
            name="applicationUrl"
            placeholder="Application URL"
            label="Application URL or Email"
            aria-label="Application URL or Email"
            description="The URL where applicants can apply or an email address. An email here will be public."
            {...form.getInputProps("applicationUrl")}
          />
          <Radio.Group name="jobType" value={jobType} onChange={setJobType} label="Type of Job">
            <Radio name="jobType" value="Full-time" label="Full-time" />
            <Radio name="jobType" value="Part-time" label="Part-time" />
            <Radio name="jobType" value="Contract" label="Contract" />
          </Radio.Group>

          <Radio.Group
            name="jobLocationType"
            value={jobLocationType}
            onChange={(value) => {
              form.clearFieldError("location");
              setJobLocationType(value);
            }}
            label="Type of Job"
          >
            <Radio name="jobLocationType" value="remote" label="Remote" />
            <Radio name="jobLocationType" value="onsite" label="Onsite" />
            <Radio name="jobLocationType" value="remoteoronsite" label="Remote or Onsite" />
          </Radio.Group>

          <TextInput
            name="location"
            placeholder={jobLocationType !== "remote" ? "Location" : "USA, Europe, etc."}
            label={jobLocationType !== "remote" ? `Location` : "Preferred Location (optional)"}
            {...form.getInputProps("location")}
          />
          <TextInput name="company" placeholder="Company Name" label="Company Name" {...form.getInputProps("company")} />
          <Box>
            <Text
              size={"sm"}
              weight={500}
              sx={(theme) => ({
                display: "inline-block",
                color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.gray[9],
                wordBreak: "break-word",
                cursor: "default",
                WebkitTapHighlightColor: "transparent",
              })}
            >
              Company Logo (optional)
            </Text>
            <Dropzone
              sx={(theme) => ({
                marginTop: 0,
              })}
              accept={[MIME_TYPES.jpeg, MIME_TYPES.png, MIME_TYPES.webp, MIME_TYPES.gif]}
              onDrop={setLogo}
              type="file"
              name="logo"
              maxSize={4000000}
              onReject={(files) => setLogoFileErrors(files?.[0]?.errors)}
              {...form.getInputProps("logo")}
            >
              <Group>
                <Dropzone.Reject>
                  <IconX size={50} stroke={1.5} color={theme.colors.red[theme.colorScheme === "dark" ? 4 : 6]} />
                </Dropzone.Reject>
                <Avatar radius={100} size={"lg"} src={companyLogoPreview}>
                  <IconPhoto />
                </Avatar>
                <Group>
                  <Button align="center" variant="outline">
                    Choose
                  </Button>
                </Group>
              </Group>
            </Dropzone>
            <Text size="xs" color="dimmed">
              Max image size of 4mb
            </Text>
            {logoFileErrors.map((err) => (
              <Text color="red" key={err.code}>
                {err.message}
              </Text>
            ))}
          </Box>

          <TextInput
            name="email"
            placeholder="Email"
            label="Your Email"
            aria-label="Your Email"
            type="email"
            description="Make sure this email is accessible by you. This is where the listing confirmation will be sent."
            {...form.getInputProps("email")}
          />
          <Group>
            <NumberInput
              name="minSalary"
              placeholder="100,000"
              label="Minimum salary (USD)"
              type="number"
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              formatter={(value) => (!Number.isNaN(parseFloat(value)) ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : null)}
              {...form.getInputProps("minSalary")}
            />
            <Box
              sx={{
                alignItems: "center",
                placeSelf: "flex-end",
              }}
            >
              <IconMinus />
            </Box>
            <NumberInput
              name="maxSalary"
              placeholder="150,000"
              label="Maximum salary (USD)"
              type="number"
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              formatter={(value) => (!Number.isNaN(parseFloat(value)) ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : null)}
              {...form.getInputProps("maxSalary")}
            />
          </Group>
          <Group position="apart" mt="md">
            <Box></Box>
            <Button size="lg" type="submit" loading={loading}>
              {`Submit Job`}
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};

export default SubmitJob;
